<template>
    <hover-card
        :height="height"
        :is-loading-metrics="isLoadingMetrics"
        :has-metrics-error="hasMetricsError"
        :metrics="metrics"
        :metrics-title="$t('Metrics')"
        :metrics-subtitle="$t('Last 30 days')">
        <template #header>
            <div class="product-area-hover-card__header">
                <pendo-tag
                    class="product-area-hover-card__tag"
                    :style="{ color: colorTagInfo.color, backgroundColor: colorTagInfo.backgroundColor }"
                    :label="title"
                    size="small"
                    subtle />
                <pendo-router-link
                    :to="viewDetailsLink"
                    class="product-area-hover-card__view-details-link"
                    target="_blank">
                    <pendo-button
                        v-pendo-tooltip="{ content: $t('View details'), position: 'top' }"
                        class="product-area-hover-card__view-details-button"
                        type="tertiary">
                        <template #default>
                            <pendo-icon
                                type="external-link"
                                size="16"
                                stroke-width="2" />
                        </template>
                    </pendo-button>
                </pendo-router-link>
            </div>
        </template>
        <template #body>
            <div class="product-area-hover-card__counts">
                <div
                    v-for="item in itemCountLabels"
                    :key="item.icon"
                    class="product-area-hover-card__count-item">
                    <pendo-icon
                        :type="item.icon"
                        size="12" />
                    <span>{{ item.count }} {{ item.label }}</span>
                </div>
            </div>
        </template>
    </hover-card>
</template>

<script>
import { PendoButton, PendoIcon, PendoTag, PendoTooltip } from '@pendo/components';
import { mapState, mapGetters, mapActions } from 'vuex';
import groupBy from 'lodash/groupBy';
import get from 'lodash/get';
import { v4 as uuid } from 'uuid';
import { sendAggregation, createRejectedStatus, loadingStatus } from '@pendo/agg-connect';
import { startWith, catchError } from 'rxjs/operators';
import HoverCard from '@/common/hover-card/hover-card.vue';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';
import { getGroupColorByCSSClass } from '@/groups/group-color-picker/group-color-service';
import { createMetricsTimeSeries, handleMetricsResponse, sendHoverCardTrackEvent } from '@/common/hover-card/hover-card-metrics.utils';
import ProductAreaMetricsAggregation from './aggs/product-area-metrics.aggregation';
import { LOADING_STATES } from '@/constants';

export default {
    name: 'ProductAreaHoverCard',
    directives: {
        PendoTooltip,
    },
    components: {
        HoverCard,
        PendoButton,
        PendoIcon,
        PendoRouterLink,
        PendoTag,
    },
    props: {
        groupId: {
            type: String,
            required: true,
        },
        height: {
            type: String,
            default: undefined,
        },
    },
    data () {
        return {
            isLoadingMetrics: true,
            hasMetricsError: false,
            visitorsValue: 0,
            accountsValue: 0,
            aggStartTime: null,
            subscription: null,
        };
    },
    computed: {
        ...mapState({
            groupsMap: (state) => state.groups.map,
        }),
        ...mapGetters({
            hasAccountLevelAnalyticsEnabled: 'subscription/hasAccountLevelAnalyticsEnabled',
        }),
        group () {
            return this.groupsMap[this.groupId] || null;
        },
        title () {
            return get(this.group, 'name', '');
        },
        colorTagInfo () {
            const colorClass = get(this.group, 'color', '.groupColorUngrouped') || '.groupColorUngrouped';
            const color = getGroupColorByCSSClass(colorClass);

            return {
                color,
                backgroundColor: `${color}1a`,
            };
        },
        itemCounts () {
            if (!this.group?.items) return { Page: 0, Feature: 0, TrackType: 0, Guide: 0, VocItem: 0 };
            const byType = groupBy(this.group.items, 'itemType');

            return {
                Page: get(byType, 'Page', []).length,
                Feature: get(byType, 'Feature', []).length,
                TrackType: get(byType, 'TrackType', []).length,
                Guide: get(byType, 'Guide', []).length,
                VocItem: get(byType, 'VocItem', []).length,
            };
        },
        itemCountLabels () {
            return [
                {
                    icon: 'page',
                    count: this.itemCounts.Page,
                    label: this.$t('productAreaHoverCard.{count, plural, one {Page} other {Pages}}', { count: this.itemCounts.Page }),
                },
                {
                    icon: 'feature',
                    count: this.itemCounts.Feature,
                    label: this.$t('productAreaHoverCard.{count, plural, one {Feature} other {Features}}', { count: this.itemCounts.Feature }),
                },
                {
                    icon: 'zap',
                    count: this.itemCounts.TrackType,
                    label: this.$t('productAreaHoverCard.{count, plural, one {Track Event} other {Track Events}}', { count: this.itemCounts.TrackType }),
                },
                {
                    icon: 'guide',
                    count: this.itemCounts.Guide,
                    label: this.$t('productAreaHoverCard.{count, plural, one {Guide} other {Guides}}', { count: this.itemCounts.Guide }),
                },
                {
                    icon: 'message-square',
                    count: this.itemCounts.VocItem,
                    label: this.$t('productAreaHoverCard.{count, plural, one {Feedback} other {Feedback}}', { count: this.itemCounts.VocItem }),
                },
            ];
        },
        metrics () {
            return [
                {
                    label: this.$t(
                        'countableHoverCard.visitorsMetric.Unique {visitorCount, plural, one {visitor} other {visitors}}',
                        { visitorCount: this.visitorsValue },
                    ),
                    icon: 'user',
                    value: this.visitorsValue,
                },
                ...(this.hasAccountLevelAnalyticsEnabled ? [{
                    label: this.$t(
                        'countableHoverCard.accountsMetric.Unique {accountCount, plural, one {account} other {accounts}}',
                        { accountCount: this.accountsValue },
                    ),
                    icon: 'users',
                    value: this.accountsValue,
                }] : []),
            ];
        },
        viewDetailsLink () {
            return { name: 'ProductAreaDetails', params: { id: this.groupId } };
        },
    },
    watch: {
        groupId: {
            handler () {
                this.fetchMetrics();
            },
        },
    },
    created () {
        this.STREAM_ID = uuid();
        this.fetchMetrics();
    },
    unmounted () {
        this.subscription?.unsubscribe();
    },
    methods: {
        ...mapActions('groups', { loadOneGroup: 'loadOne' }),
        fetchMetrics () {
            this.subscription?.unsubscribe();

            this.loadOneGroup({ id: this.groupId, items: true, reFetch: true }).catch(() => {});

            const appId = this.$store.getters['globalFilters/appId'];
            const blacklist = this.$store.getters['globalFilters/segmentBlacklist'];
            const includeAnonymous = this.$store.getters['globalFilters/segmentIncludeAnonymous'];
            const timeSeries = createMetricsTimeSeries();

            this.aggStartTime = Date.now();

            this.subscription = sendAggregation(
                ProductAreaMetricsAggregation,
                this.STREAM_ID,
                { appId, blacklist, includeAnonymous, groupId: this.groupId, timeSeries },
            ).pipe(
                startWith(loadingStatus),
                catchError((err, caught) => caught.pipe(startWith(createRejectedStatus(err)))),
            ).subscribe((response) => {
                Object.assign(this, handleMetricsResponse(response));

                if (response.status === LOADING_STATES.RESOLVED) {
                    sendHoverCardTrackEvent({
                        eventType: 'productArea',
                        aggStartTime: this.aggStartTime,
                        pageLocation: this.$route?.name,
                    });
                }
            });
        },
    },
};
</script>

<style scoped lang="less">
.product-area-hover-card {
    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        align-self: stretch;
    }

    &__tag {
        max-width: 197px;

        :deep(> span) {
            text-transform: none;
            overflow: hidden;
            text-overflow: ellipsis;
            font-weight: 400;
        }
    }

    &__view-details-link {
        display: flex;
        align-items: center;
    }

    &__view-details-button {
        height: 16px;
        color: var(--palette-gray-70);

        &:hover {
            color: var(--palette-gray-100);
        }
    }

    &__counts {
        display: flex;
        flex-direction: column;
        gap: 4px;
        font-size: 12.64px;
        color: var(--palette-gray-70);
        padding-top: 4px;
    }

    &__count-item {
        display: flex;
        align-items: center;
        gap: 6px;
    }
}
</style>
